import React, { useState, useEffect } from 'react';
import { Sparkles, LogOut } from 'lucide-react';
import useAuthStore from '../store/authStore';
import LeftPanel from './LeftPanel';
import ChatPanel from './ChatPanel';
import TeamChatPanel from './TeamChatPanel';
import ProfilePanel from './ProfilePanel';

export default function ChatApp() {
  const { user, logout } = useAuthStore();
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [leftPanelRefreshKey, setLeftPanelRefreshKey] = useState(0);
  const [chatMode, setChatMode] = useState('ai');
  const [teamUnread, setTeamUnread] = useState(0);
  const [profileOpen, setProfileOpen] = useState(false);

  // URL param auto-load: ?record=[id] or ?record=[reference_number]
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const recordParam = params.get('record');
    window.history.replaceState({}, '', window.location.pathname);

    const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/.test(recordParam);

    const navigate = (id) => {
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('panelNavigate', { detail: { record_id: id } }));
      }, 1000);
    };

    if (isUUID) {
      navigate(recordParam);
    } else {
      // Resolve reference number to UUID
      const token = localStorage.getItem('lf_token');
      fetch(`/api/records/resolve?ref=${encodeURIComponent(recordParam)}`, {
        headers: { Authorization: `Bearer ${token}` }
      })
        .then(r => r.json())
        .then(data => { if (data.record_id) navigate(data.record_id); })
        .catch(err => console.error('[URL param] resolve failed:', err));
    }
  }, []);

  // Reset to AI mode on every record change
  useEffect(() => {
    setChatMode('ai');
  }, [selectedRecord?.record_id]);

  const initials = `${(user?.first_name || '?')[0]}${(user?.last_name || '')[0] || ''}`.toUpperCase();

  return (
    <div className="h-screen w-screen bg-gray-950 flex flex-col overflow-hidden">
      {/* Top bar */}
      <div className="h-12 border-b flex items-center justify-between px-4 flex-shrink-0" style={{ background: '#0e0f11', borderBottomColor: '#2a2a35' }}>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: '#1a1710' }}>
            <Sparkles className="h-4 w-4" style={{ color: '#c9a84c' }} />
          </div>
          <span className="font-semibold text-sm" style={{ color: '#f5f0e8' }}>Sixty10 AI</span>
        </div>
        <div className="flex items-center gap-3">
          {/* Mode toggle — always visible */}
          <div style={{ display: 'flex', gap: '6px' }}>
            <button
              onClick={() => setChatMode('ai')}
              style={{
                background: chatMode === 'ai' ? '#1a1710' : 'transparent',
                border: `1px solid ${chatMode === 'ai' ? '#c9a84c' : '#3a3000'}`,
                color: chatMode === 'ai' ? '#c9a84c' : '#6b6350',
                fontSize: '10px', fontWeight: 600, padding: '4px 10px',
                borderRadius: '20px', cursor: 'pointer', transition: 'all 0.2s',
              }}
            >AI</button>
            <button
              onClick={() => { setChatMode('team'); setTeamUnread(0); }}
              style={{
                position: 'relative',
                background: chatMode === 'team' ? '#2a1a0a' : 'transparent',
                border: `1px solid ${chatMode === 'team' ? '#c9a84c' : '#3d2a1a'}`,
                color: chatMode === 'team' ? '#c9a84c' : '#8a6540',
                fontSize: '10px', fontWeight: 600, padding: '4px 10px',
                borderRadius: '20px', cursor: 'pointer', transition: 'all 0.2s',
              }}
            >
              Team
              {teamUnread > 0 && (
                <span style={{
                  position: 'absolute', top: '-4px', right: '-4px',
                  background: '#c9a84c', color: '#0e0f11',
                  fontSize: '9px', fontWeight: 700,
                  width: '16px', height: '16px', borderRadius: '50%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>{teamUnread > 9 ? '9+' : teamUnread}</span>
              )}
            </button>
          </div>
          {/* Avatar — click to open profile */}
          <button onClick={() => setProfileOpen(true)}
            style={{
              width: 28, height: 28, borderRadius: '50%', background: '#1a1710',
              border: '1px solid #3d3418', display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', fontSize: 10, fontWeight: 700, color: '#c9a84c', padding: 0,
            }}
            title="My Profile"
          >{initials}</button>
          <button onClick={logout} className="p-1.5 text-gray-500 hover:text-gray-300 rounded transition-colors" title="Sign out">
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Main layout */}
      <div className="flex-1 flex overflow-hidden">
        <LeftPanel selectedRecord={selectedRecord} onSelectRecord={setSelectedRecord} refreshKey={leftPanelRefreshKey} />
        <div style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
          {/* AI Panel */}
          <div style={{
            position: 'absolute', inset: 0,
            transform: chatMode === 'ai' ? 'translateX(0)' : 'translateX(-100%)',
            transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            zIndex: chatMode === 'ai' ? 2 : 0,
            pointerEvents: chatMode === 'ai' ? 'auto' : 'none',
            background: '#0e0f11',
          }}>
            <ChatPanel selectedRecord={selectedRecord} onSelectRecord={setSelectedRecord} onRefreshList={() => setLeftPanelRefreshKey(k => k + 1)} />
          </div>
          {/* Team Chat Panel — only mount when needed */}
          {chatMode === 'team' && (
            <div style={{ position: 'absolute', inset: 0, zIndex: 2 }}>
              <TeamChatPanel
                selectedRecord={selectedRecord}
                onUnread={(count) => { setTeamUnread(prev => prev + count); }}
              />
            </div>
          )}
        </div>
      </div>

      {/* Profile Panel */}
      <ProfilePanel open={profileOpen} onClose={() => setProfileOpen(false)} />
    </div>
  );
}
